<template>
  <div class="website-layout">

    <!-- ── Header (sticky, self-manages its own position) ── -->
    <HeaderComponent />

    <!-- ── Page content ── -->
    <main class="page-content">
      <slot />
    </main>

    <!-- ── Footer ── -->
    <FooterComponent />

  </div>
</template>

<script setup>
import { onMounted, onUnmounted, defineEmits } from 'vue';
import HeaderComponent from './components/HeaderComponent.vue';
import FooterComponent from './components/FooterComponent.vue';

/*
 * The header is position:sticky so the layout needs no margin-top offset.
 * Expose screen-size reactivity if child slots need it via provide/inject.
 */

// Optionally emit resize events for child pages that care about viewport size
const emit = defineEmits(['resize']);

let rafId = null;
function onResize() {
  cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(() => emit('resize', window.innerWidth));
}

onMounted(() => window.addEventListener('resize', onResize, { passive: true }));
onUnmounted(() => { window.removeEventListener('resize', onResize); cancelAnimationFrame(rafId); });
</script>

<style>
/* ── Global resets applied once at layout level ───────────────────────────── */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

html {
  scroll-behavior: smooth;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body {
  background: #080808;
  color: #f2ede5;
  /* Load the two typefaces used across header + footer */
  font-family: 'DM Sans', 'Segoe UI', sans-serif;
}

/* Custom scrollbar */
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: #0a0a0a; }
::-webkit-scrollbar-thumb { background: rgba(201,169,110,.3); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: rgba(201,169,110,.55); }
</style>

<style scoped>
/* ── Layout shell ─────────────────────────────────────────────────────────── */
.website-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #080808;
}

/*
 * The HeaderComponent uses position:sticky internally (top:0).
 * No top padding or margin needed here — the header sticks on scroll
 * without pushing content down with an offset hack.
 */

/* ── Main content area ────────────────────────────────────────────────────── */
.page-content {
  flex: 1;               /* fills remaining vertical space above footer */
  width: 100%;
  min-height: 60vh;      /* prevents footer creep on short pages */

  /*
   * Intentionally NO padding here — individual pages/components should
   * control their own horizontal padding so full-bleed sections work.
   * If you want a default gutter, uncomment:
   * padding: 40px 28px;
   */
}

/* ── Responsive ──────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .page-content { min-height: 50vh; }
}

@media print {
  /* Header and footer collapse; content fills the page */
  .website-layout :deep(.site-header),
  .website-layout :deep(.site-footer) { display: none; }
  .page-content { min-height: unset; }
}
</style>